import { drawBadge } from './badgeCanvas';
import { applyTextCase, fitFontSize } from './badgeText';
import { badgeCategory, resolveBadgeLayout } from './badgeLayout';
import { toFieldName } from './formInitialValues';

// Emailed certificates don't need print resolution; this keeps each PDF to a few hundred KB.
const CERTIFICATE_DPI = 150;

/** Designer field keys can be "name", a column header, or its snake_case form; offer all of them. */
export function certificateUserData(recipient, eventName) {
  const data = { eventName };
  Object.entries(recipient.data || {}).forEach(([header, value]) => {
    data[header] = value;
    data[header.toLowerCase()] = value;
    data[toFieldName(header)] = value;
  });
  data.name = recipient.name;
  data.email = recipient.email;
  return data;
}

const valueOf = (userData) => (field) => {
  const value = userData[field.key];
  if (value === undefined || value === null || value === '') return null;
  if (field.type === 'text') return applyTextCase(String(value), field);
  return /^(https?:|data:)/.test(String(value)) ? value : null;
};

export async function renderCertificate(config, userData, dpi = CERTIFICATE_DPI) {
  const { width, height } = config.paperSize;
  const layout = resolveBadgeLayout(config.fields || [], { valueOf: valueOf(userData), category: badgeCategory(userData) });
  const fields = (config.fields || [])
    .filter((field) => layout.has(field))
    .map((field) => ({ ...field, ...layout.get(field) }))
    .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');
  return drawBadge({
    widthIn: width,
    heightIn: height,
    fields,
    fontSizeFor: (field) => fitFontSize(field.value, field),
    dpi,
    background: config.backgroundImage || null,
    color: true,
  });
}

export async function certificatePdfBlob(config, userData) {
  const { jsPDF } = await import('jspdf');
  const { width, height } = config.paperSize;
  const canvas = await renderCertificate(config, userData);
  const pdf = new jsPDF({ orientation: width > height ? 'landscape' : 'portrait', unit: 'in', format: [width, height] });
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, width, height);
  return pdf.output('blob');
}

/** Runs task(item, index) over items with a small concurrency limit, reporting progress. */
export async function runLimited(items, limit, task, onProgress) {
  let next = 0;
  let done = 0;
  const failures = [];
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        await task(items[index], index);
      } catch (error) {
        failures.push({ index, error });
      }
      done += 1;
      onProgress?.(done);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return failures;
}
